import { Box, Button, Flex, Paper } from '@highlight-cp/core-ui';
import { useEffect } from 'react';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';

import classes from './guided-step-footer.module.css';
import { usePulseNextButton } from './hooks';

export type GuidedStepFooterLayout = 'fixed' | 'inline';

export interface GuidedStepFooterProps {
  onBack?: () => void;
  onNext?: () => void;
  /** When true, the Next button is omitted from the footer. */
  hideNext?: boolean;
  /**
   * `fixed` (default) — pinned to the viewport bottom with full-bleed chrome, sized to
   * `--highlight-top-nav-height`. Use for full-screen guided flows.
   * `inline` — renders in normal flow with no chrome (no fixed positioning, no background,
   * no forced height). Use as a sibling below `GuidedStepMain layout="inline"`; the consumer
   * controls outer spacing.
   */
  layout?: GuidedStepFooterLayout;
  nextDisabled?: boolean;
  /** When true, the Next button shows a loading state (and stays non-actionable). */
  nextLoading?: boolean;
}

/**
 * Nav row for a guided step. Defaults to a fixed-position full-width footer (`layout="fixed"`)
 * pinned to the viewport bottom, intended as a sibling to the main card. Pass `layout="inline"`
 * to render the same Back / Next controls in normal flow under a `GuidedStepMain layout="inline"`
 * step.
 *
 * Enter-key advance is active in both layouts whenever Next is enabled.
 * Enter is ignored when focus is on interactive controls (e.g. buttons, links, text fields)
 * so native activation / line breaks still work.
 *
 * @param params - {@link GuidedStepFooterProps}
 * @returns Footer with Back / Next when handlers are set.
 */
export const GuidedStepFooter = ({
  hideNext = false,
  layout = 'fixed',
  nextDisabled,
  nextLoading = false,
  onBack,
  onNext,
}: GuidedStepFooterProps) => {
  const showNext = onNext && !hideNext;
  const canAdvanceWithEnter = !hideNext && !nextDisabled && !nextLoading;
  const shouldPulseNextButton = usePulseNextButton({ nextDisabled, nextLoading });

  useEffect(() => {
    if (!onNext || !canAdvanceWithEnter) {
      return;
    }

    const shouldDeferEnterToFocusedControl = (event: KeyboardEvent): boolean => {
      if (event.defaultPrevented) {
        return true;
      }
      const { target } = event;
      if (!target || !(target instanceof Element)) {
        return false;
      }
      return Boolean(
        target.closest(
          'button, a[href], input, textarea, select, [contenteditable="true"], [role="combobox"]',
        ),
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return;
      }

      if (shouldDeferEnterToFocusedControl(event)) {
        return;
      }

      event.preventDefault();
      onNext();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canAdvanceWithEnter, onNext]);

  const navRow = (
    <Flex
      h="100%"
      w="100%"
      justify={!onBack || !showNext ? 'flex-end' : 'space-between'}
      align="center"
    >
      {onBack ? (
        <Button leftSection={<FiArrowLeft />} variant="outline" onClick={onBack}>
          Back
        </Button>
      ) : null}
      {showNext ? (
        <Box className={shouldPulseNextButton ? classes.nextButtonPulse : undefined}>
          <Button
            disabled={nextDisabled && !nextLoading}
            loading={nextLoading}
            rightSection={<FiArrowRight />}
            onClick={onNext}
          >
            Continue
          </Button>
        </Box>
      ) : null}
    </Flex>
  );

  if (layout === 'inline') {
    return navRow;
  }

  return (
    <Paper
      px="xl"
      bg="var(--mantine-color-body)"
      h="var(--highlight-top-nav-height)"
      w="100%"
      className={classes.footer}
    >
      {navRow}
    </Paper>
  );
};
